// src/screens/HelpSupportScreen.js
// ============================================================================
// HELP & SUPPORT — the "something's wrong / I need a human" page, linked from
// the footer on every screen. Two halves:
//   1. Self-serve answers: an expanded, issue-oriented FAQ grouped by topic
//      (payments, legacy Crits, payouts, reporting a player, region, account,
//      streaming, events) so most users never need to write in.
//   2. Contact form: signed-in users file a ticket via submitSupportRequest
//      (supportTickets collection → admin notification + support inbox email).
//      Logged-out users get a sign-in prompt + the support email as fallback.
//
// Copy must match the Terms of Service (v1.4): events are free to enter, Crits
// are retired (Legacy Crits), results and placements are never paid, and the
// only money is creator compensation from Super Crits and Rumbles, which
// CritCash pays from its own revenue. "Plan" = the platform product. No
// prizes, withdrawals, or "AI".
//
// Terms v1.5 (evergreen 3.5a): once channel memberships have ever launched
// (useMembershipsLive().everLaunched), the billing and earnings answers also
// name channel memberships and a cancel question is added (helpSectionsFor).
// Dark, helpSectionsFor(false) is HELP_SECTIONS unchanged.
//
// Terms v1.6 (evergreen 3.5b): helpSectionsFor also takes teamEverLaunched;
// the billing and compensation-source lists name team memberships once they
// have ever launched, and a team membership question is added (four states,
// see helpSectionsFor).
// ============================================================================

import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useMembershipsLive from '../hooks/useMembershipsLive';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#101010',
  BORDER: '#1c1c1c',
  GOLD: '#FCD000',
  CYAN: '#3aafff',
  GREEN: '#2ECC71',
  RED: '#ff5c5c',
  TEXT: '#EDEDED',
  DIM: '#9aa0b4',
};

// Contact-form topics — keys must match TICKET_CATEGORIES in
// functions/modules/supportTickets.js.
const TOPICS = [
  { key: 'payments_billing', label: 'Payments & Billing' },
  { key: 'crits', label: 'Legacy Crits' },
  { key: 'payouts_earnings', label: 'Payouts & Earnings' },
  { key: 'report_player', label: 'Report a Player' },
  { key: 'events_matches', label: 'Events & Matches' },
  { key: 'streaming', label: 'Streaming & Verification' },
  { key: 'account', label: 'Account & Sign-in' },
  { key: 'region_location', label: 'Region & Location' },
  { key: 'other', label: 'Something Else' },
];

// Self-serve help content. Grouped; each item is q + paragraphs. The dark
// (never launched) content; helpSectionsFor adds memberships after launch.
const HELP_SECTIONS = [
  {
    icon: 'card-outline',
    title: 'Payments & Billing',
    items: [
      {
        q: 'I was charged but my purchase never showed up',
        a: [
          "Purchases usually land within a minute. If your plan perks, Super Crit, or Rumble haven't appeared after a few minutes, sign out and back in first — that refreshes your account.",
          "Still missing? Send us a message below under Payments & Billing with the approximate time of the charge and the last 4 digits of the card. We can trace every payment and will make it right.",
        ],
      },
      {
        q: "There's a charge from CritCash I don't recognize",
        a: [
          "CritCash charges come from paid plans (monthly or yearly), Super Crits and Rumbles, and merch orders. Older statements may also show Crit packs or gift cards. Check your Wallet and Plans pages for your purchase history. A Rumble sent to an event is refunded to the payment method you used if the event is cancelled, isn't completed, or no one in it can receive a share.",
          "If it still doesn't add up, contact us below before disputing with your bank — a bank dispute locks the payment on our side and takes weeks, while we can usually resolve it in a day.",
        ],
      },
      {
        q: 'How do I cancel or change my plan?',
        a: [
          "Go to Plans from your profile. You can switch plans, move between monthly and yearly, or cancel anytime — you keep your perks until the end of the paid period.",
        ],
      },
      {
        q: 'My gift card code will not redeem',
        a: [
          "Gift cards aren't available right now, so codes can't be redeemed. If you bought a code, message us below under Payments & Billing with the order email or code and we'll take a look.",
        ],
      },
    ],
  },
  {
    icon: 'flash-outline',
    title: 'Legacy Crits',
    items: [
      {
        q: 'What are Legacy Crits?',
        a: [
          "Crits were CritCash's old entry and scoring system and are retired. Any Crits left on an account aren't money, have no cash value, can't be bought or exchanged, and may be removed.",
          "Every event is free to enter, so you never need Crits to compete.",
        ],
      },
    ],
  },
  {
    icon: 'cash-outline',
    title: 'Payouts & Earnings',
    items: [
      {
        q: "When do I get paid?",
        a: [
          "When you receive a Super Crit or a Rumble, CritCash credits you a share of the purchase, currently up to 70% after payment-processing costs (lower on small purchases). It stays pending until it's payable, no sooner than 48 hours after the purchase and not before CritCash has received the funds. An event Rumble is shared equally among the verified players who played, whatever their placement, and credited once the event's results are final.",
          "Compensation comes only from Super Crits and Rumbles. Results and placements are never paid.",
          "Payable earnings are paid automatically to the payout account you connect. You don't need a paid plan: identity and age (18+) verification is the only requirement to receive compensation, and nothing is credited before you verify.",
          "Your first bank payout can take up to a week to arrive while your account is new; after that, transfers typically land in about 2 business days.",
        ],
      },
      {
        q: "My payout didn't arrive",
        a: [
          "Check the Wallet screen: it shows whether anything is queued and whether verification or payout setup is still pending. Compensation starts crediting once your verification is complete, so it's worth verifying early — Super Crits and Rumbles from before you verified aren't credited retroactively.",
          "If Wallet says a payout was sent and it's been more than 5 business days, contact us below.",
        ],
      },
      {
        q: 'Why do you need my ID and tax info?',
        a: [
          "CritCash is an 18+ platform, and identity and age (18+) verification is the only requirement to receive compensation. Nothing is credited before you verify. You can compete and watch without it.",
          "Tax info is separate: we may ask for a Form W-9 or W-8BEN and may pause payouts until you provide it, and we report compensation to tax authorities as required by law.",
        ],
      },
    ],
  },
  {
    icon: 'shield-outline',
    title: 'Reporting a Player',
    items: [
      {
        q: 'I think my opponent was cheating',
        a: [
          "Every match on CritCash is streamed and recorded, so evidence is never lost. Report it below under Report a Player with the event link and what you saw. Our team reviews the recording and the result.",
          "Confirmed cheating means removal from the event and account suspension, and CritCash may withhold any compensation not yet paid out that came from the violation. Zero tolerance.",
        ],
      },
      {
        q: 'Someone is spamming or harassing in chat',
        a: [
          "Tap the user's name in any stream or event chat to hide or report them right there — streamers can also mute users from their own chat. Reports go straight to our team.",
          "For anything that needs more context, use the form below with a link to the stream or event.",
        ],
      },
      {
        q: 'A match result is wrong',
        a: [
          "On supported games, results are verified automatically from the stream; on other games, players report them. Disputed matches get human review. If a result posted incorrectly, report it under Events & Matches with the event link, and the recording decides it.",
        ],
      },
    ],
  },
  {
    icon: 'globe-outline',
    title: 'Region & Location',
    items: [
      {
        q: 'Where is CritCash available?',
        a: [
          "CritCash is open worldwide to players 18 and older. Prices are in USD.",
        ],
      },
    ],
  },
  {
    icon: 'person-circle-outline',
    title: 'Account & Sign-in',
    items: [
      {
        q: "I can't sign in",
        a: [
          "Use the password reset link on the login screen first. If your email changed or the reset never arrives (check spam), contact us below from any account or email support@critcash.com directly.",
        ],
      },
      {
        q: 'I want to delete or restore my account',
        a: [
          "Account deletion is in Profile → Delete Account. Your account is disabled right away and deleted after a review period; if you change your mind, email support@critcash.com within that period and we can restore it. Make sure your earnings have been paid out before you delete your account. Earnings not yet paid out may not be recoverable.",
        ],
      },
      {
        q: 'Someone else may have access to my account',
        a: [
          "Change your password immediately, then contact us below under Account & Sign-in so we can review recent activity with you.",
        ],
      },
    ],
  },
  {
    icon: 'videocam-outline',
    title: 'Streaming & Match Verification',
    items: [
      {
        q: "My stream won't connect",
        a: [
          "On Windows, download CritCast to sign in and go live (it sets up OBS and your stream key for you). For manual setup, point OBS or Streamlabs at rtmp://stream.critcash.com/live with your personal stream key (Streaming settings). One key works for every game.",
          "If the relay rejects the key, regenerate it from the Streaming screen and paste the new one into OBS.",
        ],
      },
      {
        q: 'How do I see my chat in OBS or Streamlabs?',
        a: [
          "Your chat has a pop-out page at critcash.com/chat/YourUsername that never loads the video, so it costs no stream bandwidth. In OBS Studio: Docks → Custom Browser Docks, paste that URL. In Streamlabs Desktop: Layout Editor → drag in the Website element → paste the same URL. You can also open it as a browser window from Streams → Your channel → \"Pop out your chat\".",
          "Sign in once inside the panel (it keeps its own login) so messages and pins post as you. The pop-out is owner-only — anyone else who opens it lands on your stream page.",
        ],
      },
      {
        q: "My match result wasn't detected",
        a: [
          "Detection needs a clean feed of the game — avoid heavy overlays covering the score, and keep the stream running through the end-of-match screen. Undetected results fall back to review, so your match isn't lost.",
          "If a result has been pending for a while, report it under Events & Matches with the event link.",
        ],
      },
    ],
  },
  {
    icon: 'trophy-outline',
    title: 'Events & Matches',
    items: [
      {
        q: "My opponent didn't show up",
        a: [
          "Stay in the match lobby through the ready window — no-shows are handled automatically.",
        ],
      },
      {
        q: 'I need to withdraw from an event',
        a: [
          "You can leave an event from its detail page before it locks. After lock, contact us below and we'll do what we can — brackets may already be seeded.",
        ],
      },
      {
        q: "My event sold out — why isn't it certified yet?",
        a: [
          "Sellouts certify about 48 hours after the event finishes — the window lets verification checks settle, so a wait inside that window is normal. To certify, the event has to be full-size (small casual grids don't qualify), actually finish with results, and most of the field needs to be identity-verified.",
          "Up to three sellouts a week count toward your Full Crown. Still nothing after 48 hours? Contact us below with the event name and we'll take a look.",
        ],
      },
      {
        q: "Where's my Run It Back button?",
        a: [
          "Run It Back appears on your completed event's page once its sellout certifies — about 48 hours after the event wraps. One rerun can be on the board per event at a time (it re-arms when that one finishes), and your crowd gets the call at most once a week.",
          "Team events can't Run It Back yet; team leagues can. If the button still isn't showing on a certified sellout, contact us below.",
        ],
      },
    ],
  },
];

// Paragraph swaps once memberships have ever launched (design 13.8), keyed by
// the exact dark text so the dark content stays byte-identical.
const HELP_MEMBERSHIP_SWAPS = {
  "CritCash charges come from paid plans (monthly or yearly), Super Crits and Rumbles, and merch orders. Older statements may also show Crit packs or gift cards. Check your Wallet and Plans pages for your purchase history. A Rumble sent to an event is refunded to the payment method you used if the event is cancelled, isn't completed, or no one in it can receive a share.":
    "CritCash charges come from paid plans (monthly or yearly), channel memberships (monthly), Super Crits and Rumbles, and merch orders. Older statements may also show Crit packs or gift cards. Check your Wallet, Plans and Your memberships pages for your purchase history. A Rumble sent to an event is refunded to the payment method you used if the event is cancelled, isn't completed, or no one in it can receive a share.",
  "When you receive a Super Crit or a Rumble, CritCash credits you a share of the purchase, currently up to 70% after payment-processing costs (lower on small purchases). It stays pending until it's payable, no sooner than 48 hours after the purchase and not before CritCash has received the funds. An event Rumble is shared equally among the verified players who played, whatever their placement, and credited once the event's results are final.":
    "When you receive a Super Crit or a Rumble, CritCash credits you a share of the purchase, currently up to 70% after payment-processing costs (lower on small purchases). Each membership payment to your channel, including renewals, is credited the same way. It stays pending until it's payable, no sooner than 48 hours after the purchase and not before CritCash has received the funds. An event Rumble is shared equally among the verified players who played, whatever their placement, and credited once the event's results are final.",
  "Compensation comes only from Super Crits and Rumbles. Results and placements are never paid.":
    "Compensation comes only from Super Crits, Rumbles and channel memberships. Results and placements are never paid.",
};

// Added to Payments & Billing, after the plan question, once launched.
const CANCEL_MEMBERSHIP_ITEM = {
  q: 'How do I cancel a membership?',
  a: [
    "Open Your memberships at critcash.com/memberships, pick the channel and tap Cancel membership. You keep your perks until the end of the month you paid for, and you won't be charged again.",
  ],
};
const PLAN_ITEM_Q = 'How do I cancel or change my plan?';

// Team memberships (evergreen 3.5b, design 25.8): the answers that name
// channel memberships as the whole set gain the team kind once team
// memberships have ever launched (useMembershipsLive().teamEverLaunched).
// Channel and team launched: "channel and team memberships"; team only: "team
// memberships", with no channel availability claimed (the other channel
// sentences keep their dark text). Keyed by the exact dark text.
const HELP_CHANNEL_AND_TEAM_SWAPS = {
  "CritCash charges come from paid plans (monthly or yearly), Super Crits and Rumbles, and merch orders. Older statements may also show Crit packs or gift cards. Check your Wallet and Plans pages for your purchase history. A Rumble sent to an event is refunded to the payment method you used if the event is cancelled, isn't completed, or no one in it can receive a share.":
    "CritCash charges come from paid plans (monthly or yearly), channel and team memberships (monthly), Super Crits and Rumbles, and merch orders. Older statements may also show Crit packs or gift cards. Check your Wallet, Plans and Your memberships pages for your purchase history. A Rumble sent to an event is refunded to the payment method you used if the event is cancelled, isn't completed, or no one in it can receive a share.",
  "Compensation comes only from Super Crits and Rumbles. Results and placements are never paid.":
    "Compensation comes only from Super Crits, Rumbles and channel and team memberships. Results and placements are never paid.",
};
const HELP_TEAM_ONLY_SWAPS = {
  "CritCash charges come from paid plans (monthly or yearly), Super Crits and Rumbles, and merch orders. Older statements may also show Crit packs or gift cards. Check your Wallet and Plans pages for your purchase history. A Rumble sent to an event is refunded to the payment method you used if the event is cancelled, isn't completed, or no one in it can receive a share.":
    "CritCash charges come from paid plans (monthly or yearly), team memberships (monthly), Super Crits and Rumbles, and merch orders. Older statements may also show Crit packs or gift cards. Check your Wallet, Plans and Your memberships pages for your purchase history. A Rumble sent to an event is refunded to the payment method you used if the event is cancelled, isn't completed, or no one in it can receive a share.",
  "Compensation comes only from Super Crits and Rumbles. Results and placements are never paid.":
    "Compensation comes only from Super Crits, Rumbles and team memberships. Results and placements are never paid.",
};
const CANCEL_MEMBERSHIP_ITEM_CHANNEL_AND_TEAM = {
  q: 'How do I cancel a membership?',
  a: [
    "Open Your memberships at critcash.com/memberships, pick the channel or team and tap Cancel membership. You keep your perks until the end of the month you paid for, and you won't be charged again.",
  ],
};
const CANCEL_MEMBERSHIP_ITEM_TEAM_ONLY = {
  q: 'How do I cancel a membership?',
  a: [
    "Open Your memberships at critcash.com/memberships, pick the team and tap Cancel membership. You keep your perks until the end of the month you paid for, and you won't be charged again.",
  ],
};
// Added after the cancel question while team memberships have ever launched.
const TEAM_MEMBERSHIP_ITEM = {
  q: 'What is a team membership?',
  a: [
    'A monthly membership to a team, with perks the captain chooses. Cancel anytime in Your memberships.',
  ],
};

// Four states on (channel everLaunched, teamEverLaunched): (false, false) is
// HELP_SECTIONS unchanged; (true, false) is the 3.5a launched content
// unchanged.
export function helpSectionsFor(everLaunched, teamEverLaunched = false) {
  if (!everLaunched && !teamEverLaunched) return HELP_SECTIONS;
  let swaps = HELP_MEMBERSHIP_SWAPS;
  let added = [CANCEL_MEMBERSHIP_ITEM];
  if (teamEverLaunched && everLaunched) {
    swaps = { ...HELP_MEMBERSHIP_SWAPS, ...HELP_CHANNEL_AND_TEAM_SWAPS };
    added = [CANCEL_MEMBERSHIP_ITEM_CHANNEL_AND_TEAM, TEAM_MEMBERSHIP_ITEM];
  } else if (teamEverLaunched) {
    swaps = HELP_TEAM_ONLY_SWAPS;
    added = [CANCEL_MEMBERSHIP_ITEM_TEAM_ONLY, TEAM_MEMBERSHIP_ITEM];
  }
  return HELP_SECTIONS.map((section) => ({
    ...section,
    items: section.items.flatMap((item) => {
      const next = { ...item, a: item.a.map((p) => swaps[p] || p) };
      return item.q === PLAN_ITEM_Q ? [next, ...added] : [next];
    }),
  }));
}

// --- Accordion item ---------------------------------------------------------
function HelpItem({ q, a, open, onToggle }) {
  return (
    <View style={styles.item}>
      <TouchableOpacity style={styles.qRow} onPress={onToggle} activeOpacity={0.7}>
        <Text style={styles.qText}>{q}</Text>
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={COLORS.GOLD} />
      </TouchableOpacity>
      {open && (
        <View style={styles.aWrap}>
          {a.map((p, i) => (
            <Text key={i} style={styles.aText}>{p}</Text>
          ))}
        </View>
      )}
    </View>
  );
}

export default function HelpSupportScreen({ navigation }) {
  const [openKey, setOpenKey] = useState(null);
  const { everLaunched, teamEverLaunched } = useMembershipsLive();
  const helpSections = helpSectionsFor(everLaunched, teamEverLaunched);

  // Contact form state
  const [topic, setTopic] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTicket, setSentTicket] = useState(null);
  const [formError, setFormError] = useState(null);

  const user = getAuth().currentUser;

  const submit = async () => {
    if (sending) return;
    setFormError(null);
    if (!topic) { setFormError('Pick a topic so we can route your request.'); return; }
    if (subject.trim().length < 3) { setFormError('Add a short subject.'); return; }
    if (message.trim().length < 10) { setFormError('Tell us a bit more so we can help.'); return; }
    setSending(true);
    try {
      const fn = httpsCallable(getFunctions(), 'submitSupportRequest');
      const res = await fn({ category: topic, subject: subject.trim(), message: message.trim() });
      setSentTicket(res.data?.ticketId || 'ok');
      setSubject('');
      setMessage('');
      setTopic(null);
    } catch (err) {
      setFormError(err?.message?.split(':').pop().trim() || 'Could not send your request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <View style={styles.root}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.hero}>
          <Text style={styles.eyebrow}>HELP & SUPPORT</Text>
          <Text style={styles.title}>How can we help?</Text>
          <Text style={styles.subtitle}>
            Payments, payouts, streaming, reporting a player — most answers are below,
            and a human is one message away for everything else.
          </Text>
        </View>

        {/* Self-serve sections */}
        {helpSections.map((section, si) => (
          <View key={si} style={styles.section}>
            <View style={styles.sectionHeader}>
              <Ionicons name={section.icon} size={18} color={COLORS.CYAN} />
              <Text style={styles.sectionTitle}>{section.title}</Text>
            </View>
            {section.items.map((item, ii) => {
              const key = `${si}-${ii}`;
              return (
                <HelpItem
                  key={key}
                  q={item.q}
                  a={item.a}
                  open={openKey === key}
                  onToggle={() => setOpenKey(openKey === key ? null : key)}
                />
              );
            })}
          </View>
        ))}

        {/* Contact form */}
        <View style={[styles.section, styles.contactCard]}>
          <View style={styles.sectionHeader}>
            <Ionicons name="chatbubbles-outline" size={18} color={COLORS.GOLD} />
            <Text style={styles.sectionTitle}>Still need help? Contact us</Text>
          </View>

          {sentTicket ? (
            <View style={styles.sentWrap}>
              <Ionicons name="checkmark-circle" size={36} color={COLORS.GREEN} />
              <Text style={styles.sentTitle}>Request sent</Text>
              <Text style={styles.sentBody}>
                Our team has your message and will follow up by email and in-app
                notification. Reference: {sentTicket}
              </Text>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setSentTicket(null)}>
                <Text style={styles.secondaryBtnText}>Send another request</Text>
              </TouchableOpacity>
            </View>
          ) : !user ? (
            <View style={styles.sentWrap}>
              <Text style={styles.aText}>
                Sign in to send us a message — it links your request to your
                account so we can actually fix things.
              </Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={() => navigation.navigate('Login')}>
                <Text style={styles.primaryBtnText}>Sign In</Text>
              </TouchableOpacity>
              <Text style={styles.mailFallback}>
                No account or can't sign in?{' '}
                <Text
                  style={styles.mailLink}
                  onPress={() => Linking.openURL('mailto:support@critcash.com')}
                >
                  support@critcash.com
                </Text>
              </Text>
            </View>
          ) : (
            <>
              <Text style={styles.fieldLabel}>Topic</Text>
              <View style={styles.topicWrap}>
                {TOPICS.map((t) => (
                  <TouchableOpacity
                    key={t.key}
                    style={[styles.topicChip, topic === t.key && styles.topicChipOn]}
                    onPress={() => setTopic(t.key)}
                  >
                    <Text style={[styles.topicChipText, topic === t.key && styles.topicChipTextOn]}>
                      {t.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={styles.fieldLabel}>Subject</Text>
              <TextInput
                style={styles.input}
                value={subject}
                onChangeText={setSubject}
                placeholder="One line — what's this about?"
                placeholderTextColor="#555"
                maxLength={120}
              />

              <Text style={styles.fieldLabel}>Message</Text>
              <TextInput
                style={[styles.input, styles.inputMultiline]}
                value={message}
                onChangeText={setMessage}
                placeholder="Include event links, times, and anything else that helps us find it fast."
                placeholderTextColor="#555"
                multiline
                numberOfLines={5}
                maxLength={2000}
              />

              {formError && <Text style={styles.errorText}>{formError}</Text>}

              <TouchableOpacity
                style={[styles.primaryBtn, sending && { opacity: 0.6 }]}
                onPress={submit}
                disabled={sending}
              >
                {sending
                  ? <ActivityIndicator color="#000" />
                  : <Text style={styles.primaryBtnText}>Send Request</Text>}
              </TouchableOpacity>

              <Text style={styles.mailFallback}>
                Prefer email?{' '}
                <Text
                  style={styles.mailLink}
                  onPress={() => Linking.openURL('mailto:support@critcash.com')}
                >
                  support@critcash.com
                </Text>
              </Text>
            </>
          )}
        </View>

        <Text style={styles.disclaimer}>
          For account safety we will never ask for your password. Responses go to
          your account email and in-app notifications.
        </Text>

        <Footer navigation={navigation} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { flexGrow: 1, paddingHorizontal: 20, paddingBottom: 20 },

  hero: {
    alignItems: 'center',
    paddingTop: 48,
    paddingBottom: 20,
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  eyebrow: { color: COLORS.CYAN, fontSize: 11, fontWeight: '800', letterSpacing: 2, marginBottom: 12 },
  title: { color: COLORS.TEXT, fontSize: 32, fontWeight: '900', letterSpacing: -0.5, textAlign: 'center', marginBottom: 12 },
  subtitle: { color: COLORS.DIM, fontSize: 15, lineHeight: 22, textAlign: 'center', maxWidth: 540 },

  section: {
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
    marginTop: 22,
    gap: 10,
  },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 2 },
  sectionTitle: { color: COLORS.TEXT, fontSize: 17, fontWeight: '800' },

  item: {
    backgroundColor: COLORS.CARD,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 12,
    paddingHorizontal: 16,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 18px rgba(0,0,0,0.35)' } : {}),
  },
  qRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    gap: 12,
  },
  qText: { color: COLORS.TEXT, fontSize: 15, fontWeight: '700', flex: 1 },
  aWrap: { paddingBottom: 14, gap: 10 },
  aText: { color: COLORS.DIM, fontSize: 14, lineHeight: 21 },

  contactCard: {
    backgroundColor: COLORS.CARD,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 12,
    padding: 16,
    marginTop: 30,
  },
  fieldLabel: { color: COLORS.TEXT, fontSize: 13, fontWeight: '700', marginTop: 12, marginBottom: 6 },
  topicWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  topicChip: {
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#161616',
  },
  topicChipOn: { borderColor: COLORS.GOLD, backgroundColor: 'rgba(252,208,0,0.12)' },
  topicChipText: { color: COLORS.DIM, fontSize: 13, fontWeight: '600' },
  topicChipTextOn: { color: COLORS.GOLD },
  input: {
    backgroundColor: '#161616',
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 8,
    color: COLORS.TEXT,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  inputMultiline: { minHeight: 110, textAlignVertical: 'top' },
  errorText: { color: COLORS.RED, fontSize: 13, marginTop: 10 },

  primaryBtn: {
    backgroundColor: COLORS.GOLD,
    paddingVertical: 13,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    ...(Platform.OS === 'web' ? { boxShadow: '0 0 24px rgba(252, 208, 0, 0.25)' } : {}),
  },
  primaryBtnText: { color: '#000', fontWeight: '800', fontSize: 15 },
  secondaryBtn: {
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 24,
    marginTop: 14,
  },
  secondaryBtnText: { color: COLORS.TEXT, fontWeight: '700', fontSize: 14 },

  sentWrap: { alignItems: 'center', paddingVertical: 12, gap: 8 },
  sentTitle: { color: COLORS.TEXT, fontSize: 18, fontWeight: '800' },
  sentBody: { color: COLORS.DIM, fontSize: 14, lineHeight: 21, textAlign: 'center', maxWidth: 440 },

  mailFallback: { color: '#666', fontSize: 12, textAlign: 'center', marginTop: 14 },
  mailLink: { color: COLORS.CYAN, textDecorationLine: 'underline' },

  disclaimer: {
    color: '#555',
    fontSize: 11,
    lineHeight: 16,
    textAlign: 'center',
    marginTop: 28,
    maxWidth: 540,
    alignSelf: 'center',
  },
});
